import { Component, inject } from '@angular/core';
import { AIRService } from '../../../services/air.service';
import { catchError, map, of } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';
import { HttpResponse } from '@angular/common/http';
import { FormsModule } from '@angular/forms';
import { CommonModule, DatePipe } from '@angular/common';
import { NotificationService } from '../../../services/notification.service';
import { InputTextModule } from 'primeng/inputtext';
import { InputTextareaModule } from 'primeng/inputtextarea';

@Component({
    selector: 'app-air-compliance-details',
    imports: [FormsModule, CommonModule, InputTextModule, InputTextareaModule],
    templateUrl: './air-compliance-details.component.html',
    styleUrl: './air-compliance-details.component.scss'
})
export class AirComplianceDetailsComponent {
  facilityId = localStorage.getItem('current_facility_id') || ''; // Default to 'ES' if not set
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  tagNumber: any;
  complianceData: any = {}; // Initialize as empty object to prevent undefined errors
  isLoading = true; // Add loading flag
  services: any;
  email: any;
  // Flag to determine if the AIR User ID Owner field should be editable
  airUserIdEditable: boolean = false;
  // Form model for editable fields
  airComplianceForm = {

    facilityId: '',
    tagNumber: '',
    airEmail: '',
    ldarBlueTag: '',
    repairInfo: '',
    comments: '',
    actionType: 'UPDATE', // Default value
    pnid: '',
    costCenter: '',
    lastInspectionDate: null as string | null
  };
  airByTagComplianceData: any;
  isInvalidDate: boolean = false;

  constructor(private airService: AIRService, private messageService: NotificationService) {
    this.airComplianceForm.facilityId = this.facilityId; // Set facilityId in the form model  
    // Get tagNumber from route params
    this.route.params.subscribe(params => {
      const tagNumber = params['tagNumber'];
      this.tagNumber = tagNumber;
      this.airComplianceForm.tagNumber = tagNumber; // Set the tag number in the form model
    });

    // Get actionType from localStorage using the consistent key name
    const storedActionType = localStorage.getItem('air_compliance_action_type');
    if (storedActionType) {
      this.airComplianceForm.actionType = storedActionType;
    }

    // Check if we're coming from data-editor/air route
    this.checkRouteSource();
  }

  // Check which route was used to navigate to this component
  checkRouteSource() {
    // Get the current URL
    const currentUrl = this.router.url;

    // If we're coming from data-editor/air, make the AIR User ID Owner field editable
    if (currentUrl.includes('data-editor/air')) {
      this.airUserIdEditable = true;
    } else {
      this.airUserIdEditable = false;
    }
  }

  ngOnInit() {
    this.email = sessionStorage.getItem('currentUser') || '';
    this.route.queryParams.subscribe(params => {
      if (params['tagNumber']) {
        this.tagNumber = params['tagNumber'];
      }
    });
    if (!this.tagNumber) {
      console.warn('No tag number available');
      this.isLoading = false;
      return;
    }

    this.airService.getAirComplianceDetails(this.tagNumber)
      .pipe(
        map((response: HttpResponse<any>) => {
          return response.body;
        }),
        catchError(error => {
          console.error('Error fetching compliance details:', error);
          this.isLoading = false;
          return of(null);
        })
      )
      .subscribe(data => {
        this.isLoading = false;

        if (!data) {
          console.warn('No data received from API');
          return;
        }

        if (data.tlrData && data.tlrData.length > 0) {
          // If data has the expected tlrData structure, use it
          this.complianceData = data.tlrData[0];
          this.services = data.service;
          this.airByTagComplianceData = data.airByTagComplianceData[0];
          // Populate form with existing data
          this.updateFormFromComplianceData();

        } else {
          // If data doesn't have tlrData structure, use the data directly
          this.complianceData = data;

          // Populate form with existing data
          this.updateFormFromComplianceData();

        }
      });
  }

  // Update form model from loaded compliance data
  updateFormFromComplianceData() {
    if (this.complianceData) {
      // Check if we have airByTagComplianceData and use values from there first (post-save data)
      if (this.airByTagComplianceData) {
        this.airComplianceForm.ldarBlueTag = this.airByTagComplianceData.LDARBlue || '';
        this.airComplianceForm.costCenter = this.airByTagComplianceData.CostCenter || '';
        this.airComplianceForm.pnid = this.airByTagComplianceData.PNID || '';
        this.airComplianceForm.repairInfo = this.airByTagComplianceData.RepairInfo || '';
        this.airComplianceForm.comments = this.airByTagComplianceData.Comments || '';

        // Use AIRCAI from airByTagComplianceData (the email of who created/saved the AIR record)
        // API returns the column as 'AIRCAI', not 'airEmail'
        this.airComplianceForm.airEmail = this.airByTagComplianceData.AIRCAI || '';
        
        // Convert date if available
        if (this.airByTagComplianceData.LastInspectionDate) {
          // Take only the date part before the space, ignoring the time
          this.airComplianceForm.lastInspectionDate = this.airByTagComplianceData.LastInspectionDate.split(' ')[0] || null;
        }
      } else {
        // Fall back to complianceData if airByTagComplianceData is not available
        this.airComplianceForm.ldarBlueTag = this.complianceData.ldarbluetag || '';
        this.airComplianceForm.costCenter = this.complianceData.costcenter || '';
        this.airComplianceForm.pnid = this.complianceData.pnid || '';
        this.airComplianceForm.repairInfo = this.complianceData.repair_info || '';
        this.airComplianceForm.comments = this.complianceData.comments || '';

        // Use airEmail from complianceData (the email of the tag creator, NOT the current user)
        this.airComplianceForm.airEmail = this.complianceData.airEmail || '';

        // Convert date if available
        if (this.complianceData.lastInspectionDate) {
          this.airComplianceForm.lastInspectionDate = new Date(this.complianceData.lastInspectionDate).toISOString().split('T')[0];
        }
      }

      // Preserve actionType from localStorage
      const storedActionType = localStorage.getItem('air_compliance_action_type');
      if (storedActionType) {
        this.airComplianceForm.actionType = storedActionType;
      }
    }
  }

  // Validate date format and value
  validateDate(dateString: string | null): boolean {
    if (!dateString) return true; // Allow empty value

    // Regular expression for MM/DD/YYYY, MM/DD/YY, MM-DD-YYYY, or MM-DD-YY
    const dateRegex = /^(0[1-9]|1[0-2])[\/\-](0[1-9]|[12]\d|3[01])[\/\-](19|20)?\d{2}$/;

    if (!dateRegex.test(dateString)) {
      this.isInvalidDate = true;
      return false;
    }

    // Convert to Date object to validate actual date
    const parts = dateString.split(/[\/\-]/);  // Split on either / or -
    const year = parts[2].length === 2 ? '20' + parts[2] : parts[2];
    const month = parseInt(parts[0], 10) - 1;
    const day = parseInt(parts[1], 10);
    const date = new Date(parseInt(year), month, day);

    const isValid = date.getMonth() === month &&
      date.getDate() === day &&
      date.getFullYear() === parseInt(year);

    this.isInvalidDate = !isValid;
    return isValid;
  }
  // Save form data to API
  saveDetails() {
    this.isInvalidDate = false; // Reset error state

    // Only validate if there's a date entered
    if (this.airComplianceForm.lastInspectionDate) {
      const parts = this.airComplianceForm.lastInspectionDate.split(/[\/\-]/);
      const year = parts[2].length === 2 ? '20' + parts[2] : parts[2];
      const month = parts[0].padStart(2, '0');
      const day = parts[1].padStart(2, '0');

      // Format as MM/dd/yyyy
      this.airComplianceForm.lastInspectionDate = `${month}/${day}/${year}`;
      if (!this.validateDate(this.airComplianceForm.lastInspectionDate)) {
        this.messageService.showError('Invalid date format. Please use MM/DD/YYYY or MM/DD/YY format.');
        return;
      }
    }

    // Store the email the user is trying to save for verification
    const submittedAirEmail = this.airComplianceForm.airEmail.trim();

    this.airService.saveAirComplianceDetails(this.airComplianceForm)
      .subscribe({
        next: (response) => {
          // Clear localStorage after successful save
          localStorage.removeItem('air_compliance_action_type');

          // Show success toast
          this.messageService.showSuccess('Air Compliance details saved successfully!');

          // Update the airByTagComplianceData with the new AIRCAI value to ensure form displays it correctly
          if (!this.airByTagComplianceData) {
            this.airByTagComplianceData = {};
          }
          this.airByTagComplianceData.AIRCAI = submittedAirEmail;

          // Re-populate the form with the updated data (which now includes the new AIRCAI)
          this.updateFormFromComplianceData();
        },
        error: (error) => {
          console.error('Error saving details:', error);

          // Show error toast
          this.messageService.showError('Failed to save Air Compliance details. Please try again.');
        }
      });
  }
}